import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ProgressionService from '../../services/api/ProgressionService'
import { isValidIsoDate } from '../../utils/dateHelpers'
import { updateUrlFilters, useUrlFilters } from '../../utils/urlFilters'
import SearchInput from '../ui/SearchInput'
import Select from '../ui/Select/Select'
import Tabs from '../ui/Tabs'
import Toast from '../shared/Toast'
import OperatorsActivityLogTable from './OperatorsActivityLogTable'
import ControlsAlertsChart from './ControlsAlertsChart'
import ExportMenu from './ExportMenu'
import KpiCardsRow from './KpiCardsRow'
import OperatorsProgressionTable from './OperatorsProgressionTable'
import VariableSelect from './VariableSelect'
import { useVariableOptions } from './hooks'
import useDebounceCallback from '../../hooks/useDebounceCallback'

const OPERATORS_TAB = 'operators'
const ACTIVITY_LOG_TAB = 'activity_log'
const DETAIL_TABS = [
  { value: OPERATORS_TAB, label: 'Opérateurs' },
  { value: ACTIVITY_LOG_TAB, label: 'Journal d\'activité' }
]
const TAB_PARAM = 'tab'

const emptyChart = { controls: [], alerts: [], satisfaction: [] }

const emptyMeta = hasAlertsConfigured => ({ hasAlertsConfigured, hasAdditionalAnswer: false })

const seedVariableFilter = initialVariableId => {
  if (initialVariableId && !new URLSearchParams(window.location.search).has('variable'))
    updateUrlFilters({ variable: initialVariableId })
}

const OPERATOR_SELECT_COMPONENTS = { IndicatorSeparator: null }

const SEARCH_INPUT_CLASSES = 'tw:h-9! tw:py-0!'
const EMPTY_SORT = { sortBy: null, sortDirection: null }
const TOAST_CONTAINER_ID = 'progression-toast'
const DETAIL_TABS_ROW_CLASS = 'tw:flex tw:items-center tw:justify-between tw:gap-3 tw:my-4'

const GRANULARITY_OPTIONS = [
  { value: 'day', label: 'Quotidien' },
  { value: 'week', label: 'Hebdomadaire' },
  { value: 'month', label: 'Mensuel' }
]

const DRAFT_GRANULARITY = { value: 'draft', label: 'Par planification' }
const WEEK_GRANULARITY = GRANULARITY_OPTIONS.find(option => option.value === 'week')
const MONTH_GRANULARITY = GRANULARITY_OPTIONS.find(option => option.value === 'month')

// Cross-drafts default to draft granularity (one point per planification).
// In a draft, weekly keeps several points even on a short planification.
const defaultGranularityFor = (draftId, spontaneous) => {
  if (spontaneous)
    return MONTH_GRANULARITY
  return draftId ? WEEK_GRANULARITY : DRAFT_GRANULARITY
}

const SEARCH_PARAM = 'q'

const fetchChartData = (collectId, draftId, baseParams) => {
  const measureFor = name => ProgressionService.fetchMeasure(collectId, draftId, { name, ...baseParams })
  return Promise.all([
    measureFor('controls'),
    measureFor('alerts'),
    measureFor('satisfaction')
  ]).then(([controls, alerts, satisfaction]) => ({
    controls: controls || [],
    alerts: alerts || [],
    satisfaction: satisfaction || []
  }))
}

const DISPLAY_MODES = ['absolute', 'percent']
const DEFAULT_DISPLAY_MODE = 'absolute'

const ProgressionContent = ({
  collectId, draftId, dateRangeMin, dateRangeMax, hasAlertsConfigured, initialVariableId, spontaneous,
  variables
}) => {
  // An effect would run after the table has already fetched, costing a second request.
  const variableSeeded = useRef(false)
  if (!variableSeeded.current) {
    variableSeeded.current = true
    seedVariableFilter(initialVariableId)
  }
  const filters = useUrlFilters()
  const [chartData, setChartData] = useState(emptyChart)
  const [isChartLoading, setIsChartLoading] = useState(true)
  const [meta, setMeta] = useState(() => emptyMeta(hasAlertsConfigured))
  const [rawOperatorOptions, setRawOperatorOptions] = useState([])
  const [searchInput, setSearchInput] = useState(filters[SEARCH_PARAM] || '')
  const [tableSort, setTableSort] = useState(EMPTY_SORT)
  const activeTab = filters[TAB_PARAM] === ACTIVITY_LOG_TAB ? ACTIVITY_LOG_TAB : OPERATORS_TAB
  const displayMode = DISPLAY_MODES.includes(filters.display) ? filters.display : DEFAULT_DISPLAY_MODE

  const { start_date: filterStart, end_date: filterEnd } = filters
  const dateRange = useMemo(() => ({
    startDate: isValidIsoDate(filterStart) ? filterStart : dateRangeMin,
    endDate: isValidIsoDate(filterEnd) ? filterEnd : dateRangeMax
  }), [filterStart, filterEnd, dateRangeMin, dateRangeMax])
  const operatorId = filters.operator || ''
  const variableId = filters.variable || ''
  const resourceSearch = filters[SEARCH_PARAM] || ''
  const granularityOptions = useMemo(() => {
    if (draftId || spontaneous)
      return GRANULARITY_OPTIONS
    return [...GRANULARITY_OPTIONS, DRAFT_GRANULARITY]
  }, [draftId, spontaneous])
  const granularity = useMemo(() => {
    const found = granularityOptions.find(option => option.value === filters.granularity)
    return found || defaultGranularityFor(draftId, spontaneous)
  }, [filters.granularity, granularityOptions, draftId, spontaneous])
  const { variableOptions, selectedVariable } = useVariableOptions(variables, variableId)

  useEffect(() => {
    let cancelled = false
    const { startDate, endDate } = dateRange
    const chartParams = { startDate, endDate, granularity: granularity.value }
    if (operatorId)
      chartParams.operatorId = operatorId
    if (resourceSearch)
      chartParams.resourceSearch = resourceSearch
    const guard = handler => result => {
      if (!cancelled)
        handler(result)
    }
    setIsChartLoading(true)
    fetchChartData(collectId, draftId, chartParams)
      .then(guard(setChartData))
      .catch(guard(() => setChartData(emptyChart)))
      .finally(guard(() => setIsChartLoading(false)))
    return () => { cancelled = true }
  }, [collectId, draftId, dateRange, granularity, operatorId, resourceSearch])

  useEffect(() => {
    let cancelled = false
    const guard = handler => result => {
      if (!cancelled)
        handler(result)
    }
    ProgressionService.fetchOperatorOptions(collectId, draftId)
      .then(guard(setRawOperatorOptions))
      .catch(guard(() => setRawOperatorOptions([])))
    ProgressionService.fetchMeta(collectId, draftId)
      .then(guard(payload => setMeta({
        hasAlertsConfigured: payload?.has_alerts_configured || false,
        hasAdditionalAnswer: payload?.has_additional_answer || false
      })))
      .catch(guard(() => setMeta(emptyMeta(hasAlertsConfigured))))
    return () => { cancelled = true }
  }, [collectId, draftId, hasAlertsConfigured])

  useEffect(() => {
    const next = filters[SEARCH_PARAM] || ''
    setSearchInput(current => next === current ? current : next)
  }, [filters[SEARCH_PARAM]])

  useEffect(() => {
    const tabValue = filters[TAB_PARAM]
    if (tabValue && tabValue !== ACTIVITY_LOG_TAB)
      updateUrlFilters({ [TAB_PARAM]: null })
  }, [filters[TAB_PARAM]])

  const commitSearchToUrl = useDebounceCallback(() => {
    updateUrlFilters({ [SEARCH_PARAM]: searchInput })
  }, 300)

  useEffect(() => { commitSearchToUrl() }, [searchInput, commitSearchToUrl])

  const operatorOptions = useMemo(
    () => rawOperatorOptions.map(operatorOption => (
      { value: operatorOption.user_id, label: operatorOption.user_name }
    )), [rawOperatorOptions]
  )

  const selectedOperator = useMemo(() => {
    const match = operatorOptions.find(option => String(option.value) === operatorId)
    return match || null
  }, [operatorOptions, operatorId])

  const exportParams = useMemo(() => ({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    operatorId,
    resourceSearch,
    variableId,
    sortBy: tableSort.sortBy,
    sortDirection: tableSort.sortDirection
  }), [dateRange, operatorId, resourceSearch, variableId, tableSort])

  const handleSearchChange = useCallback(event => setSearchInput(event.target.value), [])
  const handleOperatorFilterChange = useCallback(option => {
    updateUrlFilters({ operator: option?.value })
  }, [])
  const handleVariableChange = useCallback(option => {
    updateUrlFilters({ variable: option?.value })
    ProgressionService.saveVariablePreference(collectId, option?.value ?? null)
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Erreur lors de la sauvegarde de la variable de progression', error)
      })
  }, [collectId])
  const handleGranularityChange = useCallback(option => {
    const fallback = defaultGranularityFor(draftId, spontaneous).value
    const value = option ? option.value : fallback
    updateUrlFilters({ granularity: value === fallback ? null : value })
  }, [draftId, spontaneous])
  const handleDisplayModeChange = useCallback(mode => {
    updateUrlFilters({ display: mode === DEFAULT_DISPLAY_MODE ? null : mode })
  }, [])
  const showTabsVariableSelect = spontaneous && activeTab === OPERATORS_TAB
  const tabsVariableSelect = (
    <VariableSelect
      options={variableOptions}
      value={selectedVariable}
      onChange={handleVariableChange}
    />
  )
  const handleTabChange = useCallback(nextTab => {
    updateUrlFilters({ [TAB_PARAM]: nextTab === OPERATORS_TAB ? null : nextTab })
  }, [])

  return (
    <div>
      <Toast containerId={TOAST_CONTAINER_ID} />
      <KpiCardsRow
        className="tw:mb-4"
        collectId={collectId}
        dateRange={dateRange}
        draftId={draftId}
        hasAdditionalAnswer={meta.hasAdditionalAnswer}
        hasAlertsConfigured={meta.hasAlertsConfigured}
        operatorId={operatorId}
        resourceSearch={resourceSearch}
        spontaneous={spontaneous}
      />
      <div className="tw:flex tw:items-center tw:justify-end tw:mb-4 tw:gap-3">
        <SearchInput
          className="tw:grow"
          inputClassName={SEARCH_INPUT_CLASSES}
          name={SEARCH_PARAM}
          placeholder="Rechercher une ressource..."
          value={searchInput}
          onChange={handleSearchChange}
        />
        <div className="tw:w-64">
          <Select
            components={OPERATOR_SELECT_COMPONENTS}
            options={operatorOptions}
            placeholder="Choisir un opérateur"
            value={selectedOperator}
            isClearable
            onChange={handleOperatorFilterChange}
          />
        </div>
        <ExportMenu
          collectId={collectId}
          draftId={draftId}
          exportParams={exportParams}
          toastContainerId={TOAST_CONTAINER_ID}
        />
      </div>
      <ControlsAlertsChart
        alerts={chartData.alerts}
        controls={chartData.controls}
        displayMode={displayMode}
        hasAlertsConfigured={meta.hasAlertsConfigured}
        isLoading={isChartLoading}
        satisfaction={chartData.satisfaction}
        headerRight={(
          <div className="tw:w-64">
            <Select
              options={granularityOptions}
              placeholder="Pas"
              value={granularity}
              isSearchable={false}
              onChange={handleGranularityChange}
            />
          </div>
        )}
        onDisplayModeChange={handleDisplayModeChange}
      />
      <div className={spontaneous ? `${DETAIL_TABS_ROW_CLASS} tw:min-h-9` : DETAIL_TABS_ROW_CLASS}>
        <Tabs items={DETAIL_TABS} value={activeTab} onChange={handleTabChange} />
        {showTabsVariableSelect ? tabsVariableSelect : null}
      </div>
      <div className={activeTab === OPERATORS_TAB ? '' : 'tw:hidden'}>
        <OperatorsProgressionTable
          collectId={collectId}
          dateRange={dateRange}
          draftId={draftId}
          operatorId={operatorId}
          resourceSearch={resourceSearch}
          spontaneous={spontaneous}
          variableId={variableId}
          variables={variables}
          onSortChange={setTableSort}
          onVariableChange={handleVariableChange}
        />
      </div>
      <div className={activeTab === ACTIVITY_LOG_TAB ? '' : 'tw:hidden'}>
        <OperatorsActivityLogTable
          collectId={collectId}
          dateRange={dateRange}
          draftId={draftId}
          operatorId={operatorId}
          resourceSearch={resourceSearch}
          spontaneous={spontaneous}
        />
      </div>
    </div>
  )
}

export default ProgressionContent
